import { Dispatch } from '@reduxjs/toolkit';
import axios from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { janusServiceUrl } from 'src/service/clickStream.constant';
import { IClickStreamEvent } from 'src/service/clickStream.type';
import { addClickstreamEvent } from 'src/service/clickStreamEventService';
import { increaseErrorCount } from 'src/service/SyncApiErrorsService';
import { toast } from '../../web-ui-library/src/primitives/Toast/index';
import { getNextQueryParam } from '../AppRouter';
import {
  GLOBAL,
  RESOURCE_TIMING_BUFFER_SIZE,
  RESOURCE_TIMING_CLEAR_THRESHOLD
} from '../constants/Global';
import APP_ROUTES, { LOGIN_PATH } from '../layout/Routes';
import { setAuthData, setServerDate, setShowStickyNote } from '../reducers/commonSlice';
import { checkBuildTime, handleBuildNudgeInNewPortal } from './checkBuildTime';
import { isJson, isRedirectionPermitted, logCustomNegativeExperience } from './commonUtils';
import { setLocalServerTimeDiff } from './DateHelper';
import { checkHooman } from './detectBots';
import { getDeviceId, waitForDeviceId } from './FingerPrintJS';
import { clearParamsMap } from '@cp/src/components/sidebar/SideNavigationPanel/SidebarLinks';
import getPathParams from '@cp/utils/getPathParams';
import logger from './logger';
import { v4 as uuidv4 } from 'uuid';
import {
  API_LATENCY_THRESHOLD,
  API_STATUS_CODE,
  ApiExperiencePayload,
  commonErrorObject,
  responseStatus
} from './ApiHelperUtils';
import store from '@cp/src/store';

export const logError = (error?: Error | unknown, message?: string) => {
  logger({
    msg: message ?? 'Error',
    extras: {
      error: String(error) ?? 'Error not found'
    }
  });
};

export enum ApiKeys {
  SEND_OTP,
  VERIFY_OTP,
  FETCH_ALL_CASES,
  VERIFY_USER_SESSION,
  LOGOUT,
  FETCH_NOTIFICATIONS,
  NOTIFICATION_ACTION,
  SUBSCRIBE_NOTIFICATION_TOPIC,
  DETAILS,
  GLOBAL_SEARCH_CASES,
  FETCH_FEEDBACK_HISTORY,
  TELEPHONES,
  FETCH_CONTACTS,
  REGISTER_ENACH_LINK,
  CALCULATE_OVERDUE,
  PENDING_FEE_COMPONENTS,
  FEE_COMPONENT_ADJUSTMENTS,
  CUSTOMER_DOCS,
  ADJUST_COMPONENT,
  FETCH_QUESTIONS,
  FETCH_OPTIONS,
  SUBMIT_FEEDBACK,
  SUBMIT_INTERACTION_FEEDBACK,
  ADDRESSES,
  TOP_ADDRESSES,
  GET_EMI_BREAKUP,
  REPAYMENT_DUE,
  GENERATE_PAYMENT_LINK,
  SEND_PAYMENT_LINK,
  SEND_DEEPLINK,
  FETCH_SLASH_CRM_ID,
  CREATE_CALL_BRIDGE,
  CREATE_SKIP_TRACE_CALL_BRIDGE,
  FETCH_INTERACTION,
  MONTHLTY_ENACH_REQUEST,
  WA_INTERACTIONS,
  DIGITAL_COMMUNICATION,
  CALL_INTERACTIONS,
  GET_LEADERBOARD_DETAILS,
  BUILD_TIMESTAMP,
  GET_FORECLOSURE_AMOUNT,
  OAUTH_SIGNIN,
  OAUTH_EXCHANGE_SESSION,
  FETCH_CUSTOMER_INFO,
  FETCH_REPAYMENT_HISTORY,
  DOWNLOAD_DUNNING_LETTER,
  GET_PERFORMANCE,
  FIELD_FEEDBACK,
  DASHBOARD_INTERACTIONS_STATUS,
  BLANK_DISPOSITION,
  DASHBOARD_PROMISE_COUNT,
  DASHBOARD_CALLBRIDGE_DATA,
  HUMAN_REMINDER_AGENT_STATS,
  HUMAN_REMINDER_ACCEPT,
  HUMAN_REMINDER_AVAILABILITY,
  HUMAN_REMINDER_AVAILABLITY_V2,
  HUMAN_REMINDER_CUSTOMER_DETAILS,
  HUMAN_REMINDER_CALL_DISCONNECT,
  AGENT_INPUT,
  AGENT_PERFORMANCE,
  AGENCY_PERFORMANCE,
  AGENCY_INPUT,
  PD_FILTERS,
  PREVIOUS_LAN_INTERACTIONS,
  CASE_BROWSING_HISTORY,
  GET_TEAM_LEAD_CASES,
  GET_TEAM_LEAD_FILTERS,
  VERIFY_CAPTCHA,
  GET_APPROVAL_REQUESTS,
  APPROVE_ID_CARD,
  GENERATE_AMEYO_PASSWORD,
  GET_FILTER_TYPES,
  GET_FILTER_DATA,
  GET_AGENT_LOCATION_PINS,
  REPORTEES_WITH_ALLOCATION,
  ACTIVE_TELE_USERS,
  GET_REALLOCATION_HISTORY,
  REALLOCATION,
  GET_REALLOCATION_CASE_TYPES,
  GET_SINGLE_REALLOCATION,
  HEARTBEAT,
  HRC_CUSTOMER_IMAGE,
  CREATE_ENACH_REQUEST,
  UPLOAD_IMAGE_FOR_FIELD_VISIT,
  TELEPHONES_V2,
  GET_SERVER_TIME,
  STICKY_ALERT,
  FETCH_COMMITMENTS,
  FETCH_PTP_DETAILS,
  DOWNLOAD_PTP_REPORT,
  UPDATE_COMMITMENT_TRACKER,
  AGENT_CASES_LIST,
  AGENT_LOCATIONS,
  AGENT_AVAILABILITY_DETAILS,
  AGENT_AVAILABILITY,
  AGENT_AVAILABILITY_HISTORY,
  AGENT_AVAILABILITY_HISTORY_DOWNLOAD,
  FETCH_LEAVE_DETAILS,
  GET_AGENCY_SERVICEABILITY,
  GET_AGENCY_SERVICEABILITY_GET_PRESIGNED_URL,
  AGENCY_SERVICEABILITY_UPLOAD_STATUS,
  AGENCY_SERVICABILTY_SUBMIT,
  AGENCY_SERVICEABILITY_GET_STATIC_URLS,
  GET_ALL_SERVICEABLILITY_STATES,
  GET_ALL_REPORTING_AGENCIES,
  AGENCY_SERVICEABILITY_UPLOAD_ACK,
  AMEYO_UTILITY_UPLOAD_HISTORY,
  AMEYO_UTILITY_GUIDELINES,
  AMEYO_UTILITY_DOWNLOAD_FILE,
  AMEYO_UTILITY_UPLOAD_TO_AMEYO,
  AMEYO_UTILITY_DOWNLOAD_SAMPLE_FILE,
  GET_WHATSAPPCHATBOT_MESSAGES,
  POST_WHATSAPPCHATBOT_MESSAGES,
  POST_WHATSAPPCHATBOT_TRIGGERED_MESSAGES,
  WHATSPPCHATBOT_MARK_MESSAGE_READ,
  WHATSAPPCHATBOT_LOAN_AND_USER_DETAILS,
  SUBMIT_CHAT_FEEDBACK,
  RESOLVE_CHAT,
  SET_CHAT_AGENT_STATUS,
  GET_TEMPLATE_DETAILS,
  POST_WHATSAPPCHATBOT_MULTIMEDIA,
  GET_UPLOADED_MULTIMEDIA_URL,
  SHERLOCK,
  GET_AGENT_PERFORMANCE,
  GET_AGENT_INPUT,
  LEGAL_DOCS,
  GET_DEPOSITIONS,
  REGISTER_FCM_TOKEN,
  GET_DPD_BUCKET_DATA,
  GET_AGENCY_NAME_AND_STATE_OPTIONS,
  GET_AGENCY_NAME_OPTIONS,
  GET_AGENCY_DETAILS_DATA,
  GET_AGENT_PERFORMANCE_DATA,
  GET_AGENCY_DETAILS_TELE_GOVERNANCE_DATA,
  GET_AGENT_PERFORMANCE_TELE_GOVERNANCE_DATA,
  GET_AGENT_NAME_OPTIONS,
  GET_AGENT_PERFORMANCE_TL_DATA,
  GET_AGENCY_MANAGER_PERFORMANCE_DATA,
  GET_AGENCY_PERFORMANCE_DATA,
  GET_CCM_RM_OVERALL_PERFORMANCE_EXTERNAL_FILTER_OPTIONS,
  DOWNLOAD_AGENCY_MANAGER_REPORT,
  DOWNLOAD_AGENCY_REPORT,
  GET_OVERALL_OUTPUT,
  GET_OVERALL_INPUT,
  EMI_BUCKET_WISE_SUMMARY,
  GET_ALL_AGENTS,
  GET_ALL_AGENCY,
  GET_IS_EMAIL_MANDATORY,
  GET_BUCKET_GROUPS,
  GET_AVAILABLE_ROLES,
  GET_AGENT_PREFERENCES,
  GET_MANAGER_LIST,
  GET_EDIT_MANAGER_LIST,
  CREATE_AGENT_DETAILS,
  UPDATE_AGENT_DETAILS,
  CREATE_AMEYO_INTERACTION,
  EMAILS,
  GET_AGENCY_CASE_ALLOCATION_DATA,
  UPLOAD_AGENTS,
  UPLOAD_EDIT_AGENTS,
  UPLOAD_NUMBER_MANAGEMENT,
  GET_ALL_AGENTS_FILTERS,
  GEOLOCATIONS,
  SIMILAR_GEOLOCATIONS,
  GET_CSA_TICKETS,
  GET_CSA_SINGLE_TICKET,
  CREATE_TICKET,
  ACKNOWLEDGE_TICKET,
  ADD_COMMENT,
  UPDATE_TICKET_STATUS,
  GET_CSA_FILTERS,
  GET_FORM_OPTIONS,
  DOWNLOAD_BULK_SAMPLE_FILE,
  USER_UPLOAD_HISTORY,
  DOWNLOAD_FAILURE_REPORT,
  DOWNLOAD_ORIGINAL_FILE,
  DOWNLOAD_AGENCY_CASE_ALLOCATION_ORIGINAL_FILE,
  DOWNLOAD_FIELD_AGENT_APP,
  FEE_WAIVER_HISTORY,
  FEE_WAIVER_V2,
  AGENT_ONLINE_OFFLINE_STATUS,
  LIVE_LOCATION_GET_CASE_FILTERS,
  FETCH_DAILY_PLANNING_FILTERS,
  UPLOAD_CASES_ALLOCATE_DEALLOCATE,
  UPLOAD_AGENCY_CASES_ALLOCATE_DEALLOCATE,
  GET_ALLOCATION_ORIGINAL_FILE,
  GET_ALLOCATION_FAILURE_REPORT,
  REQUEST_LOAN_ACCOUNT_STATEMENT,
  PUBLISH_METRICS_TO_GRAPHANA,
  GET_BANK_STATEMENT,
  GET_BANK_STATEMENT_STATUS,
  GET_PHONE_NUMBER_STATUS,
  DOWNLOAD_CALL_RECORDING,
  DOWNLOAD_ALLOCATION_ORIGINAL_FILE,
  PARAMOUNT_GET_ALL_TENANTS,
  PRODUCTS_BY_TENATREFID,
  PARAMOUNT_CREATE_PRODUCT,
  PARAMOUNT_CREATE_SEGMENT,
  PARAMOUNT_TIMELINE,
  GET_TIMELINES_BY_TENANTREFID,
  GET_SEGMENTS_BY_TENANTREFID,
  GET_ATTRIBUTES_BY_TENANTREFID,
  GET_STRATEGIES_BY_TENANTREFID,
  GET_EXPERIMENTS_BY_TENANTREFID,
  GET_TRIGGERS_BY_TENANTREFID,
  PARAMOUNT_CREATION,
  PARAMOUNT_VALIDATION,
  PARAMOUNT_FETCH_TEMPLATE,
  PARAMOUNT_FETCH_ACTIONS,
  HUMAN_REMINDER_ACCEPT_V2,
  HUMAN_REMINDER_DISCONNECT_V2,
  HUMAN_REMINDER_GET_CASE_DETAILS_V2,
  HUMAN_REMNINDER_DISPOSE_CALL_V2,
  VALIDATE_PHONE_NUMBER,
  REMOVE_CONTACT,
  USER_ENACH_UPLOAD_HISTORY,
  GET_ENACH_UPLOAD_URL,
  ENACH_UPLOAD_ACK_URL,
  UPLOAD_ENACH_DOCUMENT,
  DOWNLOAD_ENACH_FILE,
  GET_DOCUMENT_UPLOAD_URL,
  DOCUMENT_UPLOAD_ACK_URL,
  GENERATE_DYNAMIC_DOCUMENT_URL,
  GET_MASKED_TELEPHONE,
  FETCH_AGENT_DOCUMENTS,
  GET_PAUSE_STATUS,
  PAUSE_ACTION,
  GET_OVERALL_V2,
  GET_AGENT_FCM_METRIC_V2,
  ADD_AGENCY_DETAILS,
  UPDATE_AGENCY_DETAILS,
  GET_CSA_DASHBOARD_DATA,
  GET_DME_DASHBOARD_DATA,
  ESCALATIONS,
  PAYMENT_INSTRUMENTS,
  CASE_FILTERS,
  GET_CITY_AND_PINCODE,
  SELF_SERVE_STATE_RESETTER,
  SELF_SERVE_STATE_RESETTER_SAMPLE_FILE_DOWNLOAD,
  GET_CALLING_WINDOW,
  AGENCY_CAPPING_SERVICEABILITY_DOWNLOAD,
  AGENCY_CAPPING_DASHBOARD_TABLE,
  AGENCY_CAPPING_SERVICEABILITY_UPLOAD,
  AGENCY_CAPPING_SERVICEABILITY_UPLOAD_STATUS,
  AGENCY_CAPPING_SUBMIT,
  GET_AGENCY_TYPES_MAPPING,
  GET_AVAILABLE_STATES,
  GET_AGENCY_RANKING,
  GET_ANOMALY_DETAILS,
  GET_ANOMALY_TRACKER_FILTERS,
  GET_ANOMALY_SINGLE_TICKET,
  UPDATE_ANOMALY_TICKET,
  RESOLVE_ANOMALY_TICKET,
  GET_ANOMALY_RCA_QUESTION,
  GET_ANOMALY_ACTIVITY_LOG,
  UPLOAD_NDRA,
  DOWNLOAD_NDRA_FAILURE_FILE,
  PAUSED_CASES,
  UPLOAD_MEDIA,
  UPDATE_AGENT_DRR,
  GET_AGENT_CYCLE_DETAILS,
  AGENT_DASHBOARD_DRR,
  CUSTOMER_CONFIDENCE,
  GET_PIN_CODES_DETAILS,
  ADD_NEW_ADDRESS,
  GET_CORPORATE_NUMBERS,
  GET_PAST_FEEDBACKS_ON_ADDRESSES,
  WHATSAPP_CHAT_SELFIE,
  ESCALATION_COUNT,
  GET_ACTIVE_ACTIONS,
  ACKNOWLEDGE_ACTION,
  GENERATE_REPORT,
  VIEW_REPORT_STATUS,
  DOWNLOAD_REPORT_STATUS,
  IMPERSONATION,
  GET_MOCK_RUN_HISTORY,
  INITIATE_NEW_MOCK,
  GET_INPUT_VALIDATIONS,
  GET_SINGLE_MOCK_RUN_DETAILS,
  UPDATE_MOCK_RUN_STATUS,
  FETCH_MOCK_DETAILS_BY_STATUS,
  FETCH_MOCK_RUN_CAPACITY,
  AGENT_ACTIVITY_TRACKER,
  AGENT_CALL_ACTIVITY_DATA,
  ADDRESS_INGESTION_UPLOAD_URL,
  ADDRESS_INGESTION_UPLOAD_ACK,
  ADDRESS_INGESTION_FILE_SUBMIT,
  ADDRESS_INGESTION_USER_HISTORY,
  ADDRESS_INGESTION_GET_FILE,
  ADDRESS_INGESTION_ERROR_REPORT,
  ADDRESS_INGESTION_MARK_FAILED,
  CASE_RECOMMENDATION_STATS,
  CASE_RECOMMENDATION_CURRENT_CASE,
  CASE_RECOMMENDATION_NEXT_CASE,
  CASE_RECOMMENDATION_PREVIOUS_CASE,
  CASE_RECOMMENDATION_CASES,
  CASE_RECOMMENDATION_REFRESH,
  CASE_RECOMMENDATION_LATEST_CURRENT_CASE,
  CASE_RECOMMENDATION_V2_STATS,
  CASE_RECOMMENDATION_V2_NEXT,
  CASE_RECOMMENDATION_V2_PREVIOUS,
  CASE_RECOMMENDATION_CURRENT_CASE_V2,
  GET_ALL_CASES,
  GET_VP_CASES,
  GET_VISIT_PLAN_FILTER_DATA,
  SUBMIT_VISIT_PLAN,
  ALLOCATION_EXPERIMENTS,
  ALLOCATION_EXPERIMENT_BY_ID,
  GET_CASES_GEOLOCATION_DISTANCE_FROM_AGENT_LOCATION,
  GET_COSMOS_APP_VERSION,
  GET_ACCESS_CONSENT_CHECK,
  GET_ACCESS_CONSENT_GRANT,
  GET_FIELD_CALL_INTERACTIONS,
  GET_CONFIGURE_REPORTS,
  GET_COLUMN_CONFIGURATION,
  UPDATE_EXPERIMENT_FILE,
  UPDATE_EXPERIMENT,
  GET_ACHIEVEMENT_MOVEMENT_METRICS,
  GET_GENUINE_COVERAGE_METRICS,
  GET_FIELD_ALLOCATIONS_STATUS,
  CALL_ATTEMPT_LIMIT_ALERT,
  ALLOCATION_CONFIGS_HISTORY,
  ALLOCATION_CONFIGS_LATEST,
  ALLOCATION_CONFIGS_BY_ID,
  ALLOCATION_CONFIGS_CREATE,
  ALLOCATION_CONFIGS_PREVIEW_QUERY,
  ALLOCATION_CONFIGS_DELETE_CONFIG,
  GET_ALLOCATIONS_CONFIGS,
  TRIGGER_ALLOCATION,
  GET_ALLOCATION_SUMMARY,
  RE_UPLOAD_INITIATE,
  RE_UPLOAD_SUMMARY,
  GET_ALLOCATIONS_CONFIGS_STATS,
  AUTO_GENERATE_VISIT_PLAN,
  QC_AGENT_AUDITS,
  QC_TEAM_LEAD_AUDITS,
  QC_AGENT_FILTERS,
  QC_TEAM_LEAD_FILTERS,
  CALLING_AGENT_AUDITS,
  CALLING_AGENT_AUDIT_DETAILS,
  QC_AUDIT_DETAILS,
  SUBMIT_QC_AGENT_AUDIT,
  SUBMIT_QC_TEAM_LEAD_AUDIT,
  UPDATE_RECENT_ACTIVITY,
  CALL_LIMIT_UPLOAD,
  GET_PRE_SIGNED_URL,
  SEND_UPLOAD_ACK
}

// TODO: try to get rid of `as`
const API_URLS: Record<ApiKeys, string> = {} as Record<ApiKeys, string>;

API_URLS[ApiKeys.GET_AGENCY_TYPES_MAPPING] = '/agencies/agency-types';
API_URLS[ApiKeys.GET_AGENCY_RANKING] = '/agency/{agencyCode}/ranking';
API_URLS[ApiKeys.SEND_OTP] = '/otps/send';
API_URLS[ApiKeys.VERIFY_OTP] = '/otps/verify-v2';
API_URLS[ApiKeys.FETCH_ALL_CASES] = '/allocated-cases/v1';
API_URLS[ApiKeys.VERIFY_USER_SESSION] = '/auth/user';
API_URLS[ApiKeys.FETCH_NOTIFICATIONS] = '/notification/fetch';
API_URLS[ApiKeys.NOTIFICATION_ACTION] = '/notification/action';
API_URLS[ApiKeys.FETCH_FEEDBACK_HISTORY] = '/v2/interactions/feedback-history';
API_URLS[ApiKeys.LOGOUT] = '/auth/logout';
API_URLS[ApiKeys.ADDRESSES] = '/addresses';
API_URLS[ApiKeys.TOP_ADDRESSES] = '/collection-cases/unified-locations';
API_URLS[ApiKeys.UPDATE_RECENT_ACTIVITY] = '/address/{customerReferenceId}/{addressId}';
API_URLS[ApiKeys.BUILD_TIMESTAMP] = '/version';
API_URLS[ApiKeys.FETCH_AGENT_DOCUMENTS] = '/v1/documents/agent';
API_URLS[ApiKeys.DETAILS] = '/details';
API_URLS[ApiKeys.GLOBAL_SEARCH_CASES] = '/global-search';
API_URLS[ApiKeys.FETCH_FEEDBACK_HISTORY] = '/v2/interactions/feedback-history';
API_URLS[ApiKeys.TELEPHONES] = '/telephones';
API_URLS[ApiKeys.FETCH_CONTACTS] = '/customers/contacts/{contactType}';
API_URLS[ApiKeys.REGISTER_ENACH_LINK] = '/send/register-enach-link';
API_URLS[ApiKeys.GET_EMI_BREAKUP] = 'loan/request/dues/{refId}';
API_URLS[ApiKeys.CALCULATE_OVERDUE] = '/loan/request/{lan}/overdue-charges/{date}';
API_URLS[ApiKeys.PENDING_FEE_COMPONENTS] = 'loan/request/{lan}/pending-fee-components/v1';
API_URLS[ApiKeys.FEE_COMPONENT_ADJUSTMENTS] = 'loan/request/{lan}/fee-component-adjustments';
API_URLS[ApiKeys.CUSTOMER_DOCS] = '/documents';
API_URLS[ApiKeys.LEGAL_DOCS] = '/legal-documents';
API_URLS[ApiKeys.ADJUST_COMPONENT] = '/loan/request/{lan}/adjust-component/v1';
API_URLS[ApiKeys.GET_AVAILABLE_STATES] = '/agencies/available-states';
API_URLS[ApiKeys.FETCH_QUESTIONS] = '/v2/interactions/v2/question-tree';
API_URLS[ApiKeys.FETCH_OPTIONS] = '/v2/interactions/v2/option-tree';
API_URLS[ApiKeys.SUBMIT_FEEDBACK] = '/v2/interactions/self-call';
API_URLS[ApiKeys.SUBMIT_INTERACTION_FEEDBACK] = '/v2/interactions/{interactionReferenceId}';
API_URLS[ApiKeys.CREATE_CALL_BRIDGE] = '/v2/interactions/call-bridge';
API_URLS[ApiKeys.CREATE_SKIP_TRACE_CALL_BRIDGE] = '/v2/interactions/skip-trace-call-bridge';
API_URLS[ApiKeys.FETCH_INTERACTION] = '/v2/interactions';
API_URLS[ApiKeys.CALL_INTERACTIONS] = '/v2/interactions';
API_URLS[ApiKeys.REPAYMENT_DUE] = '/loan/request/repayments/{dueReferenceId}';
API_URLS[ApiKeys.SEND_PAYMENT_LINK] = '/payments/send-payment-link';
API_URLS[ApiKeys.SEND_DEEPLINK] = '/deep-link/send';
API_URLS[ApiKeys.GENERATE_PAYMENT_LINK] = '/payments/generate-payment-link';
API_URLS[ApiKeys.FETCH_SLASH_CRM_ID] = '/users/encryptedSlashCrmId';
API_URLS[ApiKeys.MONTHLTY_ENACH_REQUEST] = 'v2/monthly-enach-request';
API_URLS[ApiKeys.WA_INTERACTIONS] = '/agent-interactions/fetch/whatsapp';
API_URLS[ApiKeys.DIGITAL_COMMUNICATION] = '/v1/digital-communication';
API_URLS[ApiKeys.GET_LEADERBOARD_DETAILS] = '/levels/agents/{agentReferenceId}/ranking';
API_URLS[ApiKeys.GET_FORECLOSURE_AMOUNT] = '/payments/{lan}/pre-closure-amount';
API_URLS[ApiKeys.FETCH_CUSTOMER_INFO] = '/customers/v1/{customerRefId}';
API_URLS[ApiKeys.FETCH_REPAYMENT_HISTORY] = 'loan-details/{loanId}/repayment-history';
API_URLS[ApiKeys.OAUTH_SIGNIN] = '/google/sign-in/url';
API_URLS[ApiKeys.OAUTH_EXCHANGE_SESSION] = '/session/exchange';
API_URLS[ApiKeys.DOWNLOAD_DUNNING_LETTER] = 'v1/documents/generate';
API_URLS[ApiKeys.GET_PERFORMANCE] = 'levels/agents/{agentReferenceId}/chart/{chartType}';
API_URLS[ApiKeys.OAUTH_SIGNIN] = '/google/sign-in/url';
API_URLS[ApiKeys.OAUTH_EXCHANGE_SESSION] = '/session/exchange';
API_URLS[ApiKeys.DASHBOARD_INTERACTIONS_STATUS] = '/dashboard/interaction-status';
API_URLS[ApiKeys.DASHBOARD_PROMISE_COUNT] = '/dashboard/promise-count';
API_URLS[ApiKeys.DASHBOARD_CALLBRIDGE_DATA] = '/dashboard/call-attempted-count';
API_URLS[ApiKeys.FIELD_FEEDBACK] = '/v2/interactions';
API_URLS[ApiKeys.BLANK_DISPOSITION] = '/v2/interactions/validate-agent-actions';
API_URLS[ApiKeys.HUMAN_REMINDER_AGENT_STATS] = '/human-reminder/agent-stats';
API_URLS[ApiKeys.HUMAN_REMINDER_ACCEPT] = '/human-reminder/accept';
API_URLS[ApiKeys.HUMAN_REMINDER_AVAILABILITY] =
  '/human-reminder/agent-activity/{agentAvailabilityStatus}/{humanReferenceId}';
API_URLS[ApiKeys.HUMAN_REMINDER_CUSTOMER_DETAILS] = '/human-reminder/{humanReferenceId}';
API_URLS[ApiKeys.HUMAN_REMINDER_CALL_DISCONNECT] =
  '/human-reminder/{humanReminderReferenceId}/disconnect';
API_URLS[ApiKeys.AGENT_INPUT] = '/longhorn/metrics/agents/input';
API_URLS[ApiKeys.AGENT_PERFORMANCE] = '/longhorn/metrics/agents/performance';
API_URLS[ApiKeys.AGENCY_PERFORMANCE] = '/longhorn/metrics/agencies/performance';
API_URLS[ApiKeys.AGENCY_INPUT] = '/longhorn/metrics/agencies/input';
API_URLS[ApiKeys.PD_FILTERS] = '/longhorn/metrics/filters';
API_URLS[ApiKeys.PREVIOUS_LAN_INTERACTIONS] = '/v2/interactions';
API_URLS[ApiKeys.VERIFY_CAPTCHA] = '/recaptcha/verify';
API_URLS[ApiKeys.CASE_BROWSING_HISTORY] = '/history/case-visit-history';
API_URLS[ApiKeys.GET_TEAM_LEAD_CASES] = '/case-allocations/all-cases';
API_URLS[ApiKeys.GET_TEAM_LEAD_FILTERS] = '/case-allocations/case-filter';
API_URLS[ApiKeys.GET_APPROVAL_REQUESTS] = '/approval-requests/pending';
API_URLS[ApiKeys.APPROVE_ID_CARD] = '/approval-requests/approval-status';
API_URLS[ApiKeys.GET_FILTER_TYPES] = '/agent/filters';
API_URLS[ApiKeys.GET_FILTER_DATA] = '/agent/filter-data';
API_URLS[ApiKeys.GET_AGENT_LOCATION_PINS] = '/agent/map-live-pins';
API_URLS[ApiKeys.REPORTEES_WITH_ALLOCATION] = '/aggregate/reportees-with-allocation';
API_URLS[ApiKeys.ACTIVE_TELE_USERS] = '/aggregate/users-with-performance';
API_URLS[ApiKeys.GET_REALLOCATION_HISTORY] = '/uploads';
API_URLS[ApiKeys.REALLOCATION] = '/case-distribution/re-allocation';
API_URLS[ApiKeys.GET_REALLOCATION_CASE_TYPES] = '/case-distribution/re-allocation/case-types';
API_URLS[ApiKeys.GET_SINGLE_REALLOCATION] = '/uploads/{referenceId}';
API_URLS[ApiKeys.HEARTBEAT] = '/heartbeat';
API_URLS[ApiKeys.GENERATE_AMEYO_PASSWORD] = '/user/ameyo/generate-password';
API_URLS[ApiKeys.HRC_CUSTOMER_IMAGE] = '/latest-selfie';
API_URLS[ApiKeys.CREATE_ENACH_REQUEST] = 'v2/create-enach-request';
API_URLS[ApiKeys.UPLOAD_IMAGE_FOR_FIELD_VISIT] = 'v2/interactions/field-visit/image';
API_URLS[ApiKeys.TELEPHONES_V2] = '/telephones/v2';
API_URLS[ApiKeys.GET_SERVER_TIME] = 'field-app/sync/server-timestamp';
API_URLS[ApiKeys.STICKY_ALERT] = '/notification/sticky';
API_URLS[ApiKeys.FETCH_COMMITMENTS] = '/longhorn/agent-performance/commitment-tracker-view';
API_URLS[ApiKeys.FETCH_PTP_DETAILS] = '/longhorn/agent-performance/ptps-due-view';
API_URLS[ApiKeys.DOWNLOAD_PTP_REPORT] = '/longhorn/agent-performance/ptps-due-view/report-url';
API_URLS[ApiKeys.UPDATE_COMMITMENT_TRACKER] =
  '/longhorn/agent-performance/daily-commitment/{commitmentReferenceId}';
API_URLS[ApiKeys.AGENT_CASES_LIST] = 'agent/allocated-case-details/{referenceId}';
API_URLS[ApiKeys.AGENT_LOCATIONS] = '/agent/location-pins/{referenceId}';
API_URLS[ApiKeys.AGENT_AVAILABILITY_DETAILS] = '/team-lead/dashboard/tl-reportees-details';
API_URLS[ApiKeys.AGENT_AVAILABILITY] =
  '/team-lead/dashboard/v2/reportees-availability/{agentReferenceId}';
API_URLS[ApiKeys.AGENT_AVAILABILITY_HISTORY] =
  '/team-lead/dashboard/v2/availability-history/{agentReferenceId}';
API_URLS[ApiKeys.AGENT_AVAILABILITY_HISTORY_DOWNLOAD] =
  '/team-lead/dashboard/v2/availability/download-history';
API_URLS[ApiKeys.FETCH_LEAVE_DETAILS] = '/users/reportees-availability';
API_URLS[ApiKeys.GET_AGENCY_SERVICEABILITY] = '/api/v1/agency-serviceability/summary';
API_URLS[ApiKeys.AGENCY_SERVICEABILITY_GET_STATIC_URLS] =
  '/api/v1/agency-serviceability/download-url';
API_URLS[ApiKeys.GET_AGENCY_SERVICEABILITY_GET_PRESIGNED_URL] =
  '/api/v1/agency-serviceability/upload-url';
API_URLS[ApiKeys.AGENCY_SERVICEABILITY_UPLOAD_STATUS] =
  '/api/v1/agency-serviceability/change-request-status';
API_URLS[ApiKeys.AGENCY_SERVICEABILITY_UPLOAD_ACK] = '/api/v1/agency-serviceability/upload-ack';
API_URLS[ApiKeys.AGENCY_SERVICABILTY_SUBMIT] = '/api/v1/agency-serviceability/apply';
API_URLS[ApiKeys.GET_ALL_SERVICEABLILITY_STATES] =
  '/api/v1/agency-serviceability/all-serviceable-states';
API_URLS[ApiKeys.GET_ALL_REPORTING_AGENCIES] =
  'api/v1/agency-serviceability/all-reporting-agencies';
API_URLS[ApiKeys.AMEYO_UTILITY_UPLOAD_HISTORY] = '/ameyo/utility/lead/upload/history';
API_URLS[ApiKeys.AMEYO_UTILITY_GUIDELINES] = '/ameyo/utility/guidelines';
API_URLS[ApiKeys.AMEYO_UTILITY_DOWNLOAD_FILE] = '/ameyo/utility/presigned-url';
API_URLS[ApiKeys.AMEYO_UTILITY_UPLOAD_TO_AMEYO] = '/ameyo/utility/lead/upload';
API_URLS[ApiKeys.AMEYO_UTILITY_DOWNLOAD_SAMPLE_FILE] = '/ameyo/utility/sample-file';
API_URLS[ApiKeys.GET_WHATSAPPCHATBOT_MESSAGES] = '/whatsapp-chat/paginate';
API_URLS[ApiKeys.POST_WHATSAPPCHATBOT_MESSAGES] = '/whatsapp-chat/reply';
API_URLS[ApiKeys.POST_WHATSAPPCHATBOT_TRIGGERED_MESSAGES] =
  '/whatsapp-chat/reply/triggered-template';
API_URLS[ApiKeys.WHATSPPCHATBOT_MARK_MESSAGE_READ] = '/whatsapp-chat/mark-read';
API_URLS[ApiKeys.WHATSAPPCHATBOT_LOAN_AND_USER_DETAILS] =
  '/whatsapp-chat/details/{loanAccountNumber}';
API_URLS[ApiKeys.SUBMIT_CHAT_FEEDBACK] = 'v2/interactions/feedback';
API_URLS[ApiKeys.RESOLVE_CHAT] = '/human-reminder-chat/resolve';
API_URLS[ApiKeys.SET_CHAT_AGENT_STATUS] = '/human-reminder-chat/{status}';
API_URLS[ApiKeys.GET_TEMPLATE_DETAILS] = '/whatsapp-chat/triggerable-chat-templates';
API_URLS[ApiKeys.POST_WHATSAPPCHATBOT_MULTIMEDIA] = '/whatsapp-chat/reply/multimedia';
API_URLS[ApiKeys.GET_UPLOADED_MULTIMEDIA_URL] =
  '/whatsapp-chat/upload-multimedia/{loanAccountNumber}';

API_URLS[ApiKeys.SHERLOCK] = '/skip-tracing';
API_URLS[ApiKeys.GET_AGENT_INPUT] = '/team-lead/dashboard/reportees-inputs';
API_URLS[ApiKeys.GET_AGENT_PERFORMANCE] = '/team-lead/dashboard/reportees-performance';
API_URLS[ApiKeys.REGISTER_FCM_TOKEN] = '/fcm';
API_URLS[ApiKeys.GET_DEPOSITIONS] = '/team-lead/dashboard/reportees-disposition-summary';
API_URLS[ApiKeys.GET_DPD_BUCKET_DATA] =
  '/longhorn/external-agency-performance-dashboard/overall-performance/overall-metrics';
API_URLS[ApiKeys.GET_AGENCY_NAME_AND_STATE_OPTIONS] =
  '/longhorn/external-agency-performance-dashboard/overall-performance/overall-metrics/filter';
API_URLS[ApiKeys.GET_AGENCY_NAME_OPTIONS] =
  '/longhorn/external-agency-performance-dashboard/field-governance/reporting-agency/filter';
API_URLS[ApiKeys.GET_AGENCY_DETAILS_DATA] =
  '/longhorn/external-agency-performance-dashboard/field-governance/agency';
API_URLS[ApiKeys.GET_AGENT_PERFORMANCE_DATA] =
  '/longhorn/external-agency-performance-dashboard/field-governance/agent';
API_URLS[ApiKeys.GET_AGENCY_DETAILS_TELE_GOVERNANCE_DATA] =
  '/longhorn/external-agency-performance-dashboard/tele-governance/agency';
API_URLS[ApiKeys.GET_AGENT_PERFORMANCE_TELE_GOVERNANCE_DATA] =
  '/longhorn/external-agency-performance-dashboard/tele-governance/agent';
API_URLS[ApiKeys.GET_AGENT_PERFORMANCE_TL_DATA] =
  '/longhorn/external-agency-performance-dashboard/overall-performance/agent-performance';
API_URLS[ApiKeys.GET_OVERALL_OUTPUT] = '/longhorn/agent-performance/overall-metrics-view/output';
API_URLS[ApiKeys.GET_OVERALL_V2] = '/longhorn/agent-performance/overall-metrics-view/v2';
API_URLS[ApiKeys.GET_AGENT_FCM_METRIC_V2] =
  '/longhorn/agent-performance/overall-metrics-view/v2/{agentId}';
API_URLS[ApiKeys.GET_OVERALL_INPUT] = '/longhorn/agent-performance/overall-metrics-view/input';
API_URLS[ApiKeys.EMI_BUCKET_WISE_SUMMARY] =
  '/team-lead/dashboard/reportees-emi-bucket-wise-summary';

API_URLS[ApiKeys.GET_ALL_AGENTS] = '/users/v3';
API_URLS[ApiKeys.GET_ALL_AGENCY] = '/agencies/v2';
API_URLS[ApiKeys.GET_IS_EMAIL_MANDATORY] = '/agent/agency-code-emails-mandatory';
API_URLS[ApiKeys.GET_BUCKET_GROUPS] = '/config/bucket-groups';
API_URLS[ApiKeys.GET_AGENT_PREFERENCES] = '/agent/preferences';
API_URLS[ApiKeys.GET_AVAILABLE_ROLES] = '/users/assignable-roles';
API_URLS[ApiKeys.GET_MANAGER_LIST] = '/reporting-managers/{refID}';
API_URLS[ApiKeys.CREATE_AGENT_DETAILS] = '/agent/v1';
API_URLS[ApiKeys.UPDATE_AGENT_DETAILS] = '/agent/{referenceId}';
API_URLS[ApiKeys.UPLOAD_AGENTS] = '/users/upload/{selectedRole}';
API_URLS[ApiKeys.UPLOAD_EDIT_AGENTS] = 'uploads/upload/edit/agents';
API_URLS[ApiKeys.UPLOAD_NUMBER_MANAGEMENT] = '/users/upload-user-number-mapping/{action}';
API_URLS[ApiKeys.GET_ALL_AGENTS_FILTERS] = '/users/filters';
API_URLS[ApiKeys.GET_EDIT_MANAGER_LIST] = '/reporting-managers/valid/{refID}';
API_URLS[ApiKeys.GET_AGENCY_MANAGER_PERFORMANCE_DATA] =
  '/longhorn/external-agency-performance-dashboard/overall-performance/agency-manager-view';
API_URLS[ApiKeys.GET_AGENCY_PERFORMANCE_DATA] =
  '/longhorn/external-agency-performance-dashboard/overall-performance/agency-view';
API_URLS[ApiKeys.GET_CCM_RM_OVERALL_PERFORMANCE_EXTERNAL_FILTER_OPTIONS] =
  '/longhorn/external-agency-performance-dashboard/overall-performance/agency-manager-view/filter';
API_URLS[ApiKeys.DOWNLOAD_AGENCY_MANAGER_REPORT] =
  '/longhorn/external-agency-performance-dashboard/overall-performance/agency-manager-report';
API_URLS[ApiKeys.DOWNLOAD_AGENCY_REPORT] =
  '/longhorn/external-agency-performance-dashboard/overall-performance/agency-report';
API_URLS[ApiKeys.GET_AGENCY_CASE_ALLOCATION_DATA] = '/agent/case-allocations';
API_URLS[ApiKeys.CREATE_AMEYO_INTERACTION] = '/v2/interactions/ameyo-interaction';
API_URLS[ApiKeys.EMAILS] = '/email';
API_URLS[ApiKeys.ADD_AGENCY_DETAILS] = '/agencies';
API_URLS[ApiKeys.UPDATE_AGENCY_DETAILS] = '/agencies/{referenceId}';
API_URLS[ApiKeys.GEOLOCATIONS] = '/unified-addresses-geolocation-details';
API_URLS[ApiKeys.SIMILAR_GEOLOCATIONS] = '/geolocation-cluster/{clusterId}/similar-locations-info';
API_URLS[ApiKeys.GET_CSA_TICKETS] = '/support-requests/fetch-all';
API_URLS[ApiKeys.GET_CSA_SINGLE_TICKET] = '/support-requests/{ticketReferenceId}';
API_URLS[ApiKeys.CREATE_TICKET] = '/support-requests';
API_URLS[ApiKeys.ACKNOWLEDGE_TICKET] = '/support-requests/{ticketReferenceId}/acknowledge';
API_URLS[ApiKeys.ADD_COMMENT] = '/support-requests/{ticketReferenceId}/comments';
API_URLS[ApiKeys.UPDATE_TICKET_STATUS] = '/support-requests/{ticketReferenceId}';
API_URLS[ApiKeys.GET_CSA_FILTERS] = '/support-requests/filters';
API_URLS[ApiKeys.GET_FORM_OPTIONS] = '/support-requests/form';
API_URLS[ApiKeys.DOWNLOAD_BULK_SAMPLE_FILE] = '/uploads/sample';
API_URLS[ApiKeys.USER_UPLOAD_HISTORY] = '/uploads/users';
API_URLS[ApiKeys.DOWNLOAD_FAILURE_REPORT] = '/uploads/users/{fileReferenceId}/failure-report';
API_URLS[ApiKeys.DOWNLOAD_ORIGINAL_FILE] = '/uploads/users/{fileReferenceId}/uploaded-file';
API_URLS[ApiKeys.DOWNLOAD_FIELD_AGENT_APP] = 'app/download?appType={appType}';
API_URLS[ApiKeys.FEE_WAIVER_HISTORY] = '/collection-cases/{loanAccountNumber}/waiver-history';
API_URLS[ApiKeys.FEE_WAIVER_V2] = '/loan/request/{loanAccountNumber}/adjust-component/v2';
API_URLS[ApiKeys.AGENT_ONLINE_OFFLINE_STATUS] = '/agent-activity';
API_URLS[ApiKeys.LIVE_LOCATION_GET_CASE_FILTERS] = 'agent/case-filter-data/{referenceId}';
API_URLS[ApiKeys.FETCH_DAILY_PLANNING_FILTERS] = '/longhorn/agent-performance/filter';
API_URLS[ApiKeys.UPLOAD_CASES_ALLOCATE_DEALLOCATE] = 'case-allocations/{agencyCode}/{type}';
API_URLS[ApiKeys.UPLOAD_AGENCY_CASES_ALLOCATE_DEALLOCATE] = 'agencies/{type}';
API_URLS[ApiKeys.GET_ALLOCATION_ORIGINAL_FILE] = 'case-allocations/sample?type={type}';
API_URLS[ApiKeys.GET_ALLOCATION_FAILURE_REPORT] = 'uploads/{referenceId}/failure-report';
API_URLS[ApiKeys.DOWNLOAD_ALLOCATION_ORIGINAL_FILE] =
  '/case-allocations/{referenceId}/uploaded-file';
API_URLS[ApiKeys.REQUEST_LOAN_ACCOUNT_STATEMENT] = '/documents/{lan}/loan-account-statement';
API_URLS[ApiKeys.PUBLISH_METRICS_TO_GRAPHANA] = '/ameyo-stats/publish-diagnostic-data';
API_URLS[ApiKeys.GET_BANK_STATEMENT] = '/documents/bank-statement/v2';
API_URLS[ApiKeys.GET_BANK_STATEMENT_STATUS] = '/documents/bank-statement/v2/status';
API_URLS[ApiKeys.GET_PHONE_NUMBER_STATUS] = '/telephones/validity';
API_URLS[ApiKeys.DOWNLOAD_CALL_RECORDING] = '/audit/download-call-recordings';
API_URLS[ApiKeys.PARAMOUNT_GET_ALL_TENANTS] = '/v1/tenant/find-all';
API_URLS[ApiKeys.PRODUCTS_BY_TENATREFID] = '/v1/product/find-by-tenant-reference-id';
API_URLS[ApiKeys.PARAMOUNT_CREATE_PRODUCT] = '/v1/product/create';
API_URLS[ApiKeys.PARAMOUNT_CREATE_SEGMENT] = '/paramount/create-segment';
API_URLS[ApiKeys.PARAMOUNT_TIMELINE] = '/v1/timeline/create';
API_URLS[ApiKeys.GET_TIMELINES_BY_TENANTREFID] = '/v1/timeline/find-by-tenant-reference-id';
API_URLS[ApiKeys.GET_SEGMENTS_BY_TENANTREFID] = '/v1/segment/find-by-tenant-reference-id';
API_URLS[ApiKeys.GET_ATTRIBUTES_BY_TENANTREFID] = '/v1/attributes/find-by-tenant-reference-id';
API_URLS[ApiKeys.GET_STRATEGIES_BY_TENANTREFID] = '/v1/strategy/find-by-tenant-reference-id';
API_URLS[ApiKeys.GET_EXPERIMENTS_BY_TENANTREFID] = '/v1/experiment/find-by-tenant-reference-id';
API_URLS[ApiKeys.PARAMOUNT_CREATION] = '/v1/collection-config/process';
API_URLS[ApiKeys.PARAMOUNT_VALIDATION] = '/v1/collection-config/test';
API_URLS[ApiKeys.GET_TRIGGERS_BY_TENANTREFID] = '/v1/trigger/find-by-tenant-reference-id';
API_URLS[ApiKeys.PARAMOUNT_FETCH_TEMPLATE] = '/v1/template/fetch-template';
API_URLS[ApiKeys.PARAMOUNT_FETCH_ACTIONS] = 'v1/action/get-valid-actions';
API_URLS[ApiKeys.HUMAN_REMINDER_ACCEPT_V2] = 'hrc/accept';
API_URLS[ApiKeys.HUMAN_REMINDER_DISCONNECT_V2] = 'hrc/disconnect';
API_URLS[ApiKeys.HUMAN_REMINDER_GET_CASE_DETAILS_V2] = '/hrc/case-details';
API_URLS[ApiKeys.HUMAN_REMNINDER_DISPOSE_CALL_V2] = '/hrc/dispose';
API_URLS[ApiKeys.HUMAN_REMINDER_AVAILABLITY_V2] =
  '/hrc/agent-activity/{agentAvailabilityStatus}/{callId}';
API_URLS[ApiKeys.VALIDATE_PHONE_NUMBER] = '/validate-existing-telephone/{number}';
API_URLS[ApiKeys.REMOVE_CONTACT] = '/global-access/telephone/remove';
API_URLS[ApiKeys.USER_ENACH_UPLOAD_HISTORY] = 'v2/file-uploads/history';
API_URLS[ApiKeys.GET_ENACH_UPLOAD_URL] = 'v2/file-uploads/upload-url';
API_URLS[ApiKeys.ENACH_UPLOAD_ACK_URL] = '/v2/file-uploads/upload-ack';
API_URLS[ApiKeys.UPLOAD_ENACH_DOCUMENT] = '/v2/file-uploads/apply/{referenceId}';
API_URLS[ApiKeys.DOWNLOAD_ENACH_FILE] = '/v2/file-uploads/download-url/{referenceId}';
API_URLS[ApiKeys.GET_DOCUMENT_UPLOAD_URL] = 'v2/file-uploads/upload-url';
API_URLS[ApiKeys.DOCUMENT_UPLOAD_ACK_URL] = '/v2/file-uploads/upload-ack';
API_URLS[ApiKeys.GET_MASKED_TELEPHONE] = 'collections/get-masked-telephone';
API_URLS[ApiKeys.GET_PAUSE_STATUS] = '/v1/case-efforts/pause/status/{caseId}';
API_URLS[ApiKeys.PAUSE_ACTION] = '/v1/case-efforts/pause/{caseId}';
API_URLS[ApiKeys.GENERATE_DYNAMIC_DOCUMENT_URL] = 'v1/documents/generate';
API_URLS[ApiKeys.GET_CSA_DASHBOARD_DATA] = 'csa-dashboard/csa-details';
API_URLS[ApiKeys.GET_DME_DASHBOARD_DATA] = 'csa-dashboard/dme-details';
API_URLS[ApiKeys.ESCALATIONS] = '/customer-escalation';
API_URLS[ApiKeys.PAYMENT_INSTRUMENTS] = '/loan-details/{customerReferenceId}/payment-instruments';
API_URLS[ApiKeys.CASE_FILTERS] = '/agent/case-filter';
API_URLS[ApiKeys.GET_CITY_AND_PINCODE] = '/address/reverse-geocode';
API_URLS[ApiKeys.SELF_SERVE_STATE_RESETTER] = 'longhorn/state-resetter/reset';
API_URLS[ApiKeys.SELF_SERVE_STATE_RESETTER_SAMPLE_FILE_DOWNLOAD] =
  'longhorn/state-resetter/sample-file';
API_URLS[ApiKeys.GET_CALLING_WINDOW] = '/longhorn/global-config';
API_URLS[ApiKeys.AGENCY_CAPPING_SERVICEABILITY_DOWNLOAD] = '/v1/agency-capping/download-url';
API_URLS[ApiKeys.AGENCY_CAPPING_DASHBOARD_TABLE] = '/v1/agency-capping/summary';
API_URLS[ApiKeys.AGENCY_CAPPING_SERVICEABILITY_UPLOAD] = '/v2/file-uploads/upload-url';
API_URLS[ApiKeys.AGENCY_CAPPING_SERVICEABILITY_UPLOAD_STATUS] =
  '/v2/file-uploads/upload-tracker-status/{referenceId}';
API_URLS[ApiKeys.AGENCY_CAPPING_SUBMIT] = '/v2/file-uploads/apply/{referenceId}';
API_URLS[ApiKeys.GET_ANOMALY_DETAILS] = '/longhorn/anomaly-tracker';
API_URLS[ApiKeys.GET_ANOMALY_TRACKER_FILTERS] = '/longhorn/anomaly-tracker/filters';
API_URLS[ApiKeys.GET_ANOMALY_SINGLE_TICKET] = '/longhorn/anomaly-tracker/{anomalyReferenceId}';
API_URLS[ApiKeys.UPDATE_ANOMALY_TICKET] = '/longhorn/anomaly-tracker/action/{anomalyReferenceId}';
API_URLS[ApiKeys.RESOLVE_ANOMALY_TICKET] = '/longhorn/anomaly-tracker/resolve/{anomalyReferenceId}';
API_URLS[ApiKeys.GET_ANOMALY_RCA_QUESTION] =
  '/longhorn/anomaly-tracker/question-tree/rca/{anomalyReferenceId}';
API_URLS[ApiKeys.GET_ANOMALY_ACTIVITY_LOG] =
  '/longhorn/anomaly-tracker/activity-logs/{anomalyReferenceId}';
API_URLS[ApiKeys.UPLOAD_NDRA] = '/agent/bulk/ndra';
API_URLS[ApiKeys.DOWNLOAD_NDRA_FAILURE_FILE] = '/uploads/v2/{referenceId}/failure-report';
API_URLS[ApiKeys.PAUSED_CASES] = '/allocated-cases/paused';
API_URLS[ApiKeys.UPLOAD_MEDIA] = 'v2/interactions/upload/media';
API_URLS[ApiKeys.UPDATE_AGENT_DRR] = 'drr-tracker/update-targets';
API_URLS[ApiKeys.GET_AGENT_CYCLE_DETAILS] = '/drr-tracker/{agentReferenceId}/cycle-options';
API_URLS[ApiKeys.AGENT_DASHBOARD_DRR] = '/drr-tracker/{agentReferenceId}/cycle/{cycleId}/overview';
API_URLS[ApiKeys.CUSTOMER_CONFIDENCE] = '/telephone-configurations';
API_URLS[ApiKeys.GET_PIN_CODES_DETAILS] = '/v1/pincodes/{pinCode}';
API_URLS[ApiKeys.ADD_NEW_ADDRESS] = '/address/proof';
API_URLS[ApiKeys.GET_CORPORATE_NUMBERS] = '/users/user-number-mapping/{agentRefId}';
API_URLS[ApiKeys.GET_PAST_FEEDBACKS_ON_ADDRESSES] = '/v2/interactions/for-locations';
API_URLS[ApiKeys.ESCALATION_COUNT] = '/agent-escalation/{agentReferenceId}/count';
API_URLS[ApiKeys.GET_ACTIVE_ACTIONS] = '/action-events/active?caseReferenceId={caseReferenceId}';
API_URLS[ApiKeys.ACKNOWLEDGE_ACTION] =
  '/action-events/acknowledge?actionReferenceId={actionReferenceId}';
API_URLS[ApiKeys.WHATSAPP_CHAT_SELFIE] =
  '/whatsapp-chat/selfie?customerReferenceId={customerReferenceId}&loanAccountNumber={loanAccountNumber}';
API_URLS[ApiKeys.GENERATE_REPORT] = '/reporting/generate-report?reportType={reportType}';
API_URLS[ApiKeys.VIEW_REPORT_STATUS] = '/reporting/fetch/active';
API_URLS[ApiKeys.DOWNLOAD_REPORT_STATUS] =
  '/reporting/download/uri?reportType={reportType}&requestId={requestId}';
API_URLS[ApiKeys.GET_MOCK_RUN_HISTORY] = '/field-allocations/self-serve/get-run-history';
API_URLS[ApiKeys.INITIATE_NEW_MOCK] = '/field-allocations/self-serve/initiate-active';
API_URLS[ApiKeys.GET_INPUT_VALIDATIONS] = '/field-allocations/self-serve/live-validations';
API_URLS[ApiKeys.GET_SINGLE_MOCK_RUN_DETAILS] =
  '/field-allocations/self-serve/{allocationTrackerId}';
API_URLS[ApiKeys.UPDATE_MOCK_RUN_STATUS] =
  '/field-allocations/self-serve/{allocationTrackerId}/update';
API_URLS[ApiKeys.FETCH_MOCK_DETAILS_BY_STATUS] = '/field-allocations/self-serve/runs-by-status';
API_URLS[ApiKeys.FETCH_MOCK_RUN_CAPACITY] = '/field-allocations/self-serve/mock-capacity';
API_URLS[ApiKeys.IMPERSONATION] = '/auth/impersonation';
API_URLS[ApiKeys.AGENT_ACTIVITY_TRACKER] = '/v1/agent-adherence-visibility/activity-dashboard';
API_URLS[ApiKeys.AGENT_CALL_ACTIVITY_DATA] = '/v1/agent-adherence-visibility/call-dashboard';
API_URLS[ApiKeys.ADDRESS_INGESTION_UPLOAD_URL] = '/address-ingestion/presigned-url';
API_URLS[ApiKeys.ADDRESS_INGESTION_UPLOAD_ACK] = '/address-ingestion/acknowledge';
API_URLS[ApiKeys.ADDRESS_INGESTION_FILE_SUBMIT] = '/address-ingestion/process';
API_URLS[ApiKeys.ADDRESS_INGESTION_USER_HISTORY] = '/address-ingestion/user-history';
API_URLS[ApiKeys.ADDRESS_INGESTION_GET_FILE] = '/address-ingestion/file';
API_URLS[ApiKeys.ADDRESS_INGESTION_ERROR_REPORT] = '/address-ingestion/error-report';
API_URLS[ApiKeys.ADDRESS_INGESTION_MARK_FAILED] = '/address-ingestion/failed';
API_URLS[ApiKeys.CASE_RECOMMENDATION_STATS] = '/case-recommendation/stats';
API_URLS[ApiKeys.CASE_RECOMMENDATION_CURRENT_CASE] = '/case-recommendation/current';
API_URLS[ApiKeys.CASE_RECOMMENDATION_NEXT_CASE] = '/case-recommendation/next';
API_URLS[ApiKeys.CASE_RECOMMENDATION_PREVIOUS_CASE] = '/case-recommendation/previous';
API_URLS[ApiKeys.CASE_RECOMMENDATION_CASES] = '/case-recommendation/all';
API_URLS[ApiKeys.CASE_RECOMMENDATION_REFRESH] = '/case-recommendation/refresh';
API_URLS[ApiKeys.CASE_RECOMMENDATION_LATEST_CURRENT_CASE] = '/case-recommendation/latest-visited';
API_URLS[ApiKeys.CASE_RECOMMENDATION_V2_STATS] = '/case-recommendation/v2/stats';
API_URLS[ApiKeys.CASE_RECOMMENDATION_V2_NEXT] = '/case-recommendation/v2/next';
API_URLS[ApiKeys.CASE_RECOMMENDATION_V2_PREVIOUS] = '/case-recommendation/v2/previous';
API_URLS[ApiKeys.CASE_RECOMMENDATION_CURRENT_CASE_V2] = '/case-recommendation/v2/current';
API_URLS[ApiKeys.GET_VP_CASES] = '/longhorn/visit-plan/agents/{agentId}';
API_URLS[ApiKeys.GET_VISIT_PLAN_FILTER_DATA] = '/longhorn/case-view/filters';
API_URLS[ApiKeys.SUBMIT_VISIT_PLAN] = '/longhorn/visit-plan/agents/{agentId}';
API_URLS[ApiKeys.GET_ALL_CASES] = '/longhorn/case-view/agents/{agentReferenceId}';
API_URLS[ApiKeys.ALLOCATION_EXPERIMENTS] = '/experiments';
API_URLS[ApiKeys.UPDATE_EXPERIMENT] = '/experiments/{experimentId}';
API_URLS[ApiKeys.ALLOCATION_EXPERIMENT_BY_ID] = '/experiments/{experimentId}';
API_URLS[ApiKeys.GET_CASES_GEOLOCATION_DISTANCE_FROM_AGENT_LOCATION] =
  '/field-app/geolocation-distance/single-source';
API_URLS[ApiKeys.GET_COSMOS_APP_VERSION] = '/app/download?appType=callingAgents';
API_URLS[ApiKeys.GET_ACCESS_CONSENT_CHECK] = '/v1/access-consent/check';
API_URLS[ApiKeys.GET_ACCESS_CONSENT_GRANT] = '/v1/access-consent/grant';
API_URLS[ApiKeys.GET_FIELD_CALL_INTERACTIONS] = '/call-recording/call-recording-details';
API_URLS[ApiKeys.GET_CONFIGURE_REPORTS] = '/reporting/configuration/config-page';
API_URLS[ApiKeys.GET_COLUMN_CONFIGURATION] = '/reporting/configuration';
API_URLS[ApiKeys.UPDATE_EXPERIMENT_FILE] = '/experiments/process-file';
API_URLS[ApiKeys.GET_GENUINE_COVERAGE_METRICS] =
  '/longhorn/external-agency-performance-dashboard/agency-performance/input-metrics';
API_URLS[ApiKeys.GET_ACHIEVEMENT_MOVEMENT_METRICS] =
  '/longhorn/external-agency-performance-dashboard/agency-performance/output-metrics';
API_URLS[ApiKeys.GET_FIELD_ALLOCATIONS_STATUS] =
  '/field-allocations/self-serve/mock-controls/status';
API_URLS[ApiKeys.CALL_ATTEMPT_LIMIT_ALERT] = '/telephones/call-attempt-limit-alert';
API_URLS[ApiKeys.SUBSCRIBE_NOTIFICATION_TOPIC] = '/notification/subscription/{topic}';
API_URLS[ApiKeys.ALLOCATION_CONFIGS_HISTORY] = '/allocations/ciu/configs/all';
API_URLS[ApiKeys.ALLOCATION_CONFIGS_LATEST] = '/allocations/ciu/configs/latest';
API_URLS[ApiKeys.ALLOCATION_CONFIGS_BY_ID] = '/allocations/ciu/configs/{configId}';
API_URLS[ApiKeys.ALLOCATION_CONFIGS_CREATE] = '/allocations/ciu/configs';
API_URLS[ApiKeys.ALLOCATION_CONFIGS_PREVIEW_QUERY] = '/allocations/ciu/configs/preview-query';
API_URLS[ApiKeys.ALLOCATION_CONFIGS_DELETE_CONFIG] = '/allocations/ciu/configs/{configId}';
API_URLS[ApiKeys.GET_ALLOCATIONS_CONFIGS] = '/tele/self-serve/configs';
API_URLS[ApiKeys.TRIGGER_ALLOCATION] = '/tele/self-serve/trigger';
API_URLS[ApiKeys.GET_ALLOCATION_SUMMARY] = '/tele/self-serve/summary';
API_URLS[ApiKeys.RE_UPLOAD_INITIATE] = '/ameyo/re-upload/initiate';
API_URLS[ApiKeys.RE_UPLOAD_SUMMARY] = '/ameyo/re-upload/summary';
API_URLS[ApiKeys.QC_AGENT_AUDITS] = '/audit/px-call/qc-agent/audits';
API_URLS[ApiKeys.QC_TEAM_LEAD_AUDITS] = '/audit/px-call/qc-tl/audits';
API_URLS[ApiKeys.QC_AGENT_FILTERS] = '/audit/px-call/qc-agent/filters';
API_URLS[ApiKeys.QC_TEAM_LEAD_FILTERS] = '/audit/px-call/qc-tl/filters';
API_URLS[ApiKeys.CALLING_AGENT_AUDITS] = '/audit/px-call/calling-agent/audits';
API_URLS[ApiKeys.CALLING_AGENT_AUDIT_DETAILS] =
  '/audit/px-call/calling-agent/audit/{auditAssignmentReferenceId}';
API_URLS[ApiKeys.QC_AUDIT_DETAILS] =
  '/audit/px-call/qc-agent-tl/audit/{auditAssignmentReferenceId}';
API_URLS[ApiKeys.SUBMIT_QC_AGENT_AUDIT] =
  '/audit/px-call/qc-agent/audit/{auditAssignmentReferenceId}';
API_URLS[ApiKeys.SUBMIT_QC_TEAM_LEAD_AUDIT] =
  '/audit/px-call/qc-tl/audit/{auditAssignmentReferenceId}';
API_URLS[ApiKeys.GET_ALLOCATIONS_CONFIGS_STATS] = '/allocations/ciu/configs/stats';
API_URLS[ApiKeys.AUTO_GENERATE_VISIT_PLAN] = '/longhorn/visit-plan/agents/{agentId}/generate';
API_URLS[ApiKeys.CALL_LIMIT_UPLOAD] = '/v1/call-count-experiment/upload';
API_URLS[ApiKeys.GET_PRE_SIGNED_URL] =
  '/agent-data-sync/presigned-url?userReferenceId={agentID}&deviceReferenceId={deviceID}&dataSyncType={dataSyncType}';
API_URLS[ApiKeys.SEND_UPLOAD_ACK] = '/agent-data-sync/{requestId}';

export let navigate: NavigateFunction;
export let dispatch: Dispatch<any>;

export const getErrorMessage = (err: any) => {
  if (err?.response?.data?.title) {
    return err?.response?.data?.title;
  }

  try {
    const errorContent = err?.response?.data?.message
      ? JSON.parse(err?.response?.data?.message)
      : '';
    return (
      errorContent?.detail ||
      errorContent?.message ||
      errorContent ||
      err?.message ||
      'Something went wrong!'
    );
  } catch (ex) {
    logError(ex);
    return err?.message || 'Something went wrong!';
  }
};

export function getApiUrl(
  apiKey: ApiKeys,
  params?: Record<string, string | number> | null,
  queryParams?: Record<string, string | number>
) {
  let apiUrl = API_URLS[apiKey];

  // replace all {placeholders} with their values in params
  if (params) {
    Object.keys(params).forEach(paramKey => {
      try {
        apiUrl = apiUrl.replaceAll(`{${paramKey}}`, `${params[paramKey]}`);
      } catch (error) {
        apiUrl = apiUrl.replace(new RegExp(`{${paramKey}}`, 'g'), `${params[paramKey]}`);
      }
    });
  }

  if (queryParams) {
    apiUrl +=
      '?' +
      Object.keys(queryParams)
        .map(key => `${key}=${queryParams[key]}`)
        .join('&');
  }

  return `${apiUrl}`;
}

const getEventName = (status: number): IClickStreamEvent => {
  return Object.values(responseStatus).reduce((acc, curr) => {
    return curr.min <= status && status <= curr.max ? curr.eventName : acc;
  }, {} as IClickStreamEvent);
};

// status code to be retried on
const errorsToRetry = [503];

// APIs to exclude from negative experience tracking
const NEGATIVE_EXPERIENCE_WHITELIST = [janusServiceUrl, '/events/json', '/cybertron-logs'];

const shouldExcludeFromNegativeExperience = (url: string): boolean => {
  return NEGATIVE_EXPERIENCE_WHITELIST.some(whitelistedUrl => url?.includes(whitelistedUrl));
};

const axiosInstance = axios.create();

axiosInstance.interceptors.request.use(
  async request => {
    if (!checkHooman()) {
      return {
        ...request,
        signal: AbortSignal.abort()
      };
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // request['retry'] = request['retry'] && request['retry'] < 4 ? request['retry'] : 3;
    request.retry = request?.retry < 4 ? request.retry : 3;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    request.delay = request?.delay > 2000 ? request.delay : 2000;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    request.headers['request-start-time'] = new Date().getTime();
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    request.headers['X-Session-Id'] = GLOBAL.SESSION_TOKEN || '';
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    request.headers['agent_business_vertical'] = GLOBAL?.AGENT_BUSINESS_VERTICAL || '';
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    request.headers['X-Device-Id'] = getDeviceId() || (await waitForDeviceId());

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    request.headers['X-Idempotency-Key'] = uuidv4();

    const extraHeaders = getPathParams(APP_ROUTES.CASE_DETAIL.path);

    if (extraHeaders.caseBusinessVertical && request?.headers) {
      request.headers['caseBusinessVertical'] = extraHeaders?.caseBusinessVertical;
    }
    if (extraHeaders.caseReferenceId && request?.headers) {
      request.headers['caseReferenceId'] = extraHeaders?.caseReferenceId;
    }

    return request;
  },
  function (error) {
    return Promise.reject(error);
  }
);

performance.setResourceTimingBufferSize(RESOURCE_TIMING_BUFFER_SIZE);

let clearScheduled = false;
const scheduleClearResourceTimings = () => {
  if (clearScheduled) return;
  if (performance.getEntriesByType('resource').length >= RESOURCE_TIMING_CLEAR_THRESHOLD) {
    clearScheduled = true;
    setTimeout(() => {
      performance.clearResourceTimings();
      clearScheduled = false;
    }, 0);
  }
};

const getActualLatency = (responseURL: string | undefined, fallbackStart: number): number => {
  if (responseURL) {
    const entries = performance.getEntriesByName(
      responseURL,
      'resource'
    ) as PerformanceResourceTiming[];
    if (entries.length > 0) {
      const latency = Math.round(entries[entries.length - 1].duration);
      scheduleClearResourceTimings();
      return latency;
    }
  }
  scheduleClearResourceTimings();
  return Date.now() - fallbackStart;
};

axiosInstance.interceptors.response.use(
  response => {
    if (!checkHooman()) return { ...response, data: null, error: commonErrorObject };

    let latency = 0;

    if (response?.config?.headers) {
      const start = Number(response.config.headers['request-start-time']);
      latency = getActualLatency(response?.request?.responseURL, start);
      response.headers['request-duration'] = String(latency);
    }

    if (response?.config?.headers && response?.headers?.['cf-connecting-ip']) {
      window.clientIp = response?.headers?.['cf-connecting-ip'];
    }

    if (response?.headers?.date) {
      const serverTime = new Date(response.headers['date']).getTime();
      const localServerTimeDiff = serverTime - Date.now();
      setLocalServerTimeDiff(localServerTimeDiff);
      store.dispatch(setServerDate(response.headers['date']));
    }
    const payload = { buildTime: Number(response.headers['build_time']) };
    checkBuildTime(payload, handleBuildNudgeInNewPortal(dispatch, setShowStickyNote));

    const includeInUptimeMetrics = response.config.headers?.includeInUptimeMetrics;

    if (includeInUptimeMetrics) {
      logApiExperience({
        latency,
        url: response?.config?.url ?? '',
        status: response?.status ?? 0,
        idempotencyKey: response?.config?.headers?.['X-Idempotency-Key'] as string
      });
    }
    return response;
  },
  error => {
    if (error?.code === 'ERR_CANCELED') {
      const newErr = commonErrorObject;
      return Promise.reject(newErr);
    }

    const { config, response } = error;
    const idempotencyKey = response?.config?.headers?.['X-Idempotency-Key'];

    const startTime = response?.config?.headers?.['request-start-time'];
    const milliseconds = startTime
      ? getActualLatency(response?.request?.responseURL, Number(startTime))
      : 0;

    if (response?.config?.url !== janusServiceUrl) {
      const errorMessage = response?.data?.message || '';
      const eventMessage = isJson(errorMessage) || errorMessage;
      const eventName = getEventName(response?.status);
      addClickstreamEvent(eventName as IClickStreamEvent, {
        timeTaken: milliseconds,
        status: response?.status,
        endPoint: response?.config?.url,
        message: eventMessage
      });
    }

    if (response?.status >= API_STATUS_CODE.INTERNAL_SERVER_ERROR) {
      increaseErrorCount();
    }

    if (!config || config.retry <= 1 || !errorsToRetry.includes(error?.response?.status)) {
      const errorString = getErrorMessage(error);
      if (!config?.headers?.donotHandleError) {
        toast(errorString, { type: 'error' });
      }

      if (response?.status === API_STATUS_CODE.FORBIDDEN) {
        dispatch && dispatch(setAuthData({ token: '' }));
        clearParamsMap();
        if (isRedirectionPermitted()) {
          navigate && navigate(`${LOGIN_PATH}?next=${getNextQueryParam()}`);
        }
      }

      return Promise.reject(error);
    }
    config.retry -= 1;
    const delayRetryRequest = new Promise<void>(resolve => {
      setTimeout(() => {
        resolve();
      }, config.delay);
    });
    config.delay *= 2;
    logger({
      msg: 'api response error',
      extras: {
        type: 'API_ERROR',
        endpoint: response?.config?.url ?? '',
        method: response?.config?.method ?? '',
        error: error.message,
        idempotencyKey
      }
    });

    const includeInUptimeMetrics = response.config.headers?.includeInUptimeMetrics;
    if (includeInUptimeMetrics) {
      logApiExperience({
        latency: milliseconds,
        url: response?.config?.url ?? '',
        status: response?.status ?? 0,
        idempotencyKey: idempotencyKey ?? ''
      });
    }

    return delayRetryRequest.then(() => axiosInstance(config));
  }
);

const logApiExperience = (payload: ApiExperiencePayload) => {
  const { latency, url, status, idempotencyKey } = payload;

  if (shouldExcludeFromNegativeExperience(url)) {
    return;
  }

  if (status >= API_STATUS_CODE.INTERNAL_SERVER_ERROR) {
    logCustomNegativeExperience({
      message: `API response error: ${status}`,
      extras: {
        latency,
        url,
        status,
        idempotencyKey
      }
    });
  }

  if (latency >= API_LATENCY_THRESHOLD) {
    logCustomNegativeExperience({
      message: `API response latency: ${latency}`,
      extras: {
        latency,
        url,
        status,
        idempotencyKey
      }
    });
  }
};
axiosInstance.defaults.headers.common['Content-Type'] = 'application/json';
axiosInstance.defaults.baseURL = `${window?.config?.BFF_SERVICE_BASE_URL || '/api'}`;
axiosInstance.defaults.headers.common['routing_key'] = '';
// TODO:: Ideally should happen through middlewares.
export const registerNavigateAndDispatch = (
  navigateParam: NavigateFunction,
  dispatchParam: Dispatch<any>
) => ((navigate = navigateParam), (dispatch = dispatchParam));

export default axiosInstance;
